import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { queryMergeOrder, sendMergeVerifyCode } from "../../../api/order";
import "./index.scss";
import OrderInfo from "../../../pages/Pay/OrderInfo";
import {
  mergeOrderListStore,
  mergeOrderSelectIdsStore,
} from "../../../store/order";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { Button, Checkbox, Col, Flex, Form, Input, Row, message } from "antd";
import { IS_MOBILE } from "@/utils/tool";
import { OrderStore } from "@/store/order";
import sentry4Action from "@/utils/sentry";
import useLanguage from "@/hooks/useLanguage";

type Props = {
  order: Order;
  cancelMerge: (isMerge) => void;
};
const default_count = 60;
/**
 * 作者其它订单列表
 * @returns
 */
const AuthOtherOrder = (props: Props) => {
  const { order, cancelMerge } = props;
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [code, setCode] = useState("");
  const [selectIds, setSelectIds] = useState<Array<string>>([]);
  const [allSelected, setAllSelected] = useState(false);
  const [mergeOrderList, setMergeOrderList] =
    useRecoilState(mergeOrderListStore);
  const setSelectIdsStore = useSetRecoilState(mergeOrderSelectIdsStore);
  const timer = useRef(null);
  const [loading, setLoading] = useState(false);
  const [codeLoading, setCodeLoading] = useState(false);
  // 倒计时秒数
  const [count, setCount] = useState(default_count);
  const rawOrderInfo = useRecoilValue(OrderStore);

  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  /**
   * 获取验证码
   */
  const getCode = async () => {
    sentry4Action("合并作者其他订单-获取验证码", {
      order_id: order.order_id,
      order_type: order.expense_type,
      publishing_house_name: order.publishing_house_name,
      journal_name: order.journal_name,
      article_title: order.article_title,
      cae: order.contact_author_email.split("@").join(" "),
      meeting_name: order?.meeting_name,
    });
    if (codeLoading) return;
    setCodeLoading(true);

    timer.current = setInterval(() => {
      setCount((count) => --count);
    }, 1000);
    const res = await sendMergeVerifyCode<{ status: number; internal: number }>(
      { order_id: order.order_id }
    );
    if (res.code === 0) {
      if (res.data.status === 2) {
        messageApi.error(t("验证码已发送"));
        setCount(0);
      }
      return;
    }
    messageApi.error(res.msg);
    setCount(0);
  };

  /**
   * 获取合并订单列表
   */
  const getMergeOrder = useCallback(async () => {
    sentry4Action("合并作者其他订单-获取订单", {
      order_id: order.order_id,
      order_type: order.expense_type,
      publishing_house_name: order.publishing_house_name,
      journal_name: order.journal_name,
      article_title: order.article_title,
      cae: order.contact_author_email.split("@").join(" "),
      meeting_name: order?.meeting_name,
    });
    try {
      const fields = await form.validateFields();
      setLoading(true);
      const res = await queryMergeOrder<Array<Order>>({
        order_id: order.order_id,
        verify_code: fields.code,
      });
      setLoading(false);
      if (res.code === 0) {
        const { configuration } = rawOrderInfo;
        const finalData = res.data.map((item) => ({
          ...item,
          configuration: configuration,
        }));
        setMergeOrderList(finalData);
        if (!finalData.length) {
          messageApi.info(t("该作者暂无符合条件的可合并订单"));
          return;
        }
        return;
      } else {
        if (res.code === 200002) {
          location.reload();
          return;
        }
      }
      messageApi.error(res.msg);
    } catch (error) {}
  }, [code, code]);

  /**
   * 更新选中状态
   * @param v
   * @param orderId
   * @returns
   */
  const updateCheck = (v: boolean, orderId) => {
    if (v) {
      if (selectIds.includes(orderId)) return;
      setSelectIds((ids) => [...ids, orderId]);
      return;
    }
    if (selectIds.includes(orderId)) {
      const filterIds = selectIds.filter((id) => id !== orderId);
      setSelectIds(filterIds);
    }
  };

  const onChangeAllSelect = () => {
    const value = !allSelected;
    setAllSelected(value);
    if (value) {
      const ids = mergeOrderList.map((item) => item.order_id);
      setSelectIds(ids);
      return;
    }
    setSelectIds([]);
  };

  /**
   * 拆分订单成功后更新订单
   * @param newOrder
   * @param index
   */
  const splitOrderSuccess = (newOrder: Order, index: number) => {
    const beforeList = mergeOrderList.slice(0, index);
    const endList = mergeOrderList.slice(index + 1);
    const curItem = newOrder;
    setMergeOrderList([...beforeList, curItem, ...endList]);
  };

  useEffect(() => {
    setSelectIdsStore(selectIds);
  }, [selectIds]);

  useEffect(() => {
    setAllSelected(selectIds.length === mergeOrderList.length);
  }, [selectIds, mergeOrderList]);

  useEffect(() => {
    if (count <= 0) {
      setCount(default_count);
      clearInterval(timer.current);
      setCodeLoading(false);
    }
  }, [count]);
  return (
    <>
      {contextHolder}
      <div
        className={`auth-order-box ${IS_MOBILE ? "mobile-auth-order-box" : ""}`}
      >
        {mergeOrderList.length === 0 && (
          <div className="search-box">
            <div className="content">
              <div>
                <span className="title">{t("邮箱")}:</span>
                <span>
                  {order.publishing_house_order_type === 4
                    ? order.book_contact_email
                    : order.contact_author_email}
                </span>
              </div>
              <div>
                <Form
                  form={form}
                  layout={
                    IS_MOBILE
                      ? language === "en"
                        ? "vertical"
                        : "inline"
                      : "horizontal"
                  }
                >
                  <Form.Item
                    label={<span className="title">{t("验证码")}</span>}
                    style={{
                      marginBottom: 0,
                      //   display: `${language === "en" ? "flex" : ""}`,
                    }}
                  >
                    <Flex>
                      {/* <Row>
                      <Col > */}
                      <Form.Item
                        name="code"
                        style={{ marginBottom: 0 }}
                        rules={[
                          { required: true, message: t("请输入验证码") },
                          {
                            pattern: new RegExp(/^\d{6}$/i),
                            message: t("输入的验证码错误"),
                          },
                        ]}
                        className={`${IS_MOBILE && language === "zh" ? "form-item-code" : ""}`}
                      >
                        <Input
                          className={`code ${language}`}
                          type="text"
                          placeholder={t("请输入")}
                          onChange={(e) => setCode(e.target.value)}
                          style={IS_MOBILE ? { width: "10rem" } : {}}
                        />
                      </Form.Item>
                      {/* </Col>
                      <Col> */}
                      <Button
                        type="default"
                        onClick={getCode}
                        className={`query-code ${
                          codeLoading ? "disabled" : ""
                        } `}
                        style={IS_MOBILE ? { marginLeft: 10 } : {}}
                      >
                        {codeLoading
                          ? `(${t("已发送")})${count}s`
                          : t("获取验证码")}
                      </Button>
                      {/* </Col>
                    </Row> */}
                    </Flex>
                  </Form.Item>
                </Form>
              </div>
            </div>
            <div className="btn-wrapper">
              <Button
                type="primary"
                className="search-button"
                loading={loading}
                onClick={getMergeOrder}
              >
                {t("获取订单")}
              </Button>
              <Button
                className="cancel-button"
                onClick={() => cancelMerge(false)}
              >
                {t("取消")}
              </Button>
            </div>
          </div>
        )}
        {mergeOrderList.length > 0 && (
          <div>
            <div className="auth-head-title">
              <span> {t("合并支付其他订单")}</span>
              <div className="all-select-box" onChange={onChangeAllSelect}>
                <Checkbox
                  checked={allSelected}
                  onChange={onChangeAllSelect}
                  indeterminate={
                    selectIds.length > 0 &&
                    selectIds.length < mergeOrderList.length
                  }
                >
                  {t("全选")}
                </Checkbox>
              </div>
              <span className="count">
                {t("SelectedCount", {
                  select: selectIds.length,
                  total: mergeOrderList.length,
                })}
              </span>
            </div>
            <div className="order-list">
              {mergeOrderList.map((_order, index) => {
                return (
                  <div key={_order.order_id} className="merge-order-info">
                    <OrderInfo
                      order={_order}
                      isCheck={true}
                      updateCheck={(v) => updateCheck(v, _order.order_id)}
                      splitOrderSuccess={(newOrder) =>
                        splitOrderSuccess(newOrder, index)
                      }
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AuthOtherOrder;
